/**
 * NHI (National Health Number) citizen registration API client.
 *
 * Pinned contract (implemented by Dex on the backend):
 *   POST /api/v1/nhi/register
 *     body: the wizard's collected data (hasID, nationalID?, names incl.
 *           father/mother, gender, dob, place_of_birth, civilGov/placeOfReg/
 *           civilRegNo/maritalStatus, docType/docNumber/docIssuer (no-ID alt
 *           route), mobile/email/address fields, declare/dataProcessing flags)
 *     response:
 *       { outcome: 'existing' | 'pending_review' | 'success',
 *         nhn?, ref_number?, assurance?, verification_status? }
 *
 * Outcome semantics:
 *   - existing       → definite EMPI match: show the existing NHN, no new number
 *   - pending_review → possible duplicate: routed to identity resolution, ref shown
 *   - success        → new NHN issued (permanent; verification improves assurance only)
 */

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  (typeof window !== 'undefined'
    ? `${window.location.origin}/api/v1`
    : 'http://127.0.0.1:8000/api/v1')

export type NhiOutcome = 'existing' | 'pending_review' | 'success'

export interface NhiRegisterPayload {
  hasID: boolean
  nationalID?: string
  firstName: string
  fatherName: string
  familyName: string
  motherName: string
  gender: string
  dob: string
  placeOfBirth: string
  civilGov?: string
  placeOfReg?: string
  civilRegNo?: string
  maritalStatus?: string
  docType?: string
  docNumber?: string
  docIssuer?: string
  mobile?: string
  email?: string
  governorate?: string
  city?: string
  neighborhood?: string
  street?: string
  buildingNo?: string
  otpVerified?: boolean
  identityVerified?: boolean
  declare: boolean
  dataProcessing: boolean
}

export interface NhiRegisterResponse {
  outcome: NhiOutcome
  nhn?: string
  nhn_masked?: string
  reference_number?: string
  assurance?: string
  message?: string
}

export class NhiApiError extends Error {
  status: number
  detail: unknown
  constructor(status: number, detail: unknown) {
    super(typeof detail === 'string' ? detail : `Request failed (${status})`)
    this.name = 'NhiApiError'
    this.status = status
    this.detail = detail
  }
}

export async function postNhiRegister(
  payload: NhiRegisterPayload,
  options: { signal?: AbortSignal } = {},
): Promise<NhiRegisterResponse> {
  const body = {
    has_national_id: payload.hasID,
    national_id: payload.nationalID ?? null,
    first_name: payload.firstName,
    father_name: payload.fatherName,
    family_name: payload.familyName,
    mother_name: payload.motherName || null,
    gender: payload.gender,
    dob: payload.dob,
    place_of_birth: payload.placeOfBirth,
    alt_doc_type: payload.docType ?? null,
    alt_doc_number: payload.docNumber ?? null,
    alt_doc_issuer: payload.docIssuer ?? null,
    civil_governorate: payload.civilGov || payload.governorate || '',
    place_of_registration: payload.placeOfReg ?? null,
    civil_reg_no: payload.civilRegNo ?? null,
    marital_status: payload.maritalStatus ?? null,
    email: payload.email ?? null,
    mobile: payload.mobile ?? null,
    governorate: payload.governorate || payload.civilGov || '',
    city: payload.city || '',
    neighborhood: payload.neighborhood ?? null,
    street: payload.street ?? null,
    building_no: payload.buildingNo ?? null,
    declared_correct: payload.declare,
    acknowledge_processing: payload.dataProcessing,
  }
  const response = await fetch(`${API_BASE}/nhi/register`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal: options.signal,
  })
  if (!response.ok) {
    let detail: unknown = response.statusText
    try {
      const body = await response.json()
      detail = body.detail ?? body
    } catch {
      /* non-JSON error body */
    }
    throw new NhiApiError(response.status, detail)
  }
  return (await response.json()) as NhiRegisterResponse
}
